import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowOutward, LockOutlined } from "@mui/icons-material";
import { cloneElement, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import BlockedModal from "../../../componentes/blockedModal";

export default function ProductCard({
  name,
  description,
  logo,
  icon,
  clientImg,
  logoHeight = 30,
  accent,
  href,
  navigateTo,
  productName,
  disabled = false,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { settings } = useOutletContext() || {};
  const [blockedModal, setBlockedModal] = useState(false);

  console.log(settings, productName);

  const moduleFlags =
    (Array.isArray(settings)
      ? settings.find((i) => i?.produto === "modulos")?.configuracoes
      : null) || {};
  const isBlocked = productName && moduleFlags?.[productName] === false;

  const handleOpen = (newTab = false) => {
    if (disabled) return;
    if (isBlocked) {
      setBlockedModal(true);
      return;
    }
    if (navigateTo) {
      if (newTab) window.open(navigateTo, "_blank");
      else navigate(navigateTo);
      return;
    }
    if (href) window.open(href, "_blank");
  };

  const interactive = !disabled;

  const styles = {
    card: {
      position: "relative",
      overflow: "hidden",
      height: "100%",
      minHeight: 104,
      p: 2,
      pl: 2.25,
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
      bgcolor: theme.palette.background.paper,
      boxShadow: "0px 1px 3px rgba(0,0,0,0.06)",
      cursor: interactive ? "pointer" : "default",
      opacity: disabled ? 0.55 : 1,
      transition:
        "transform .18s ease, box-shadow .18s ease, border-color .18s",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        background: accent,
        opacity: 0.9,
      },
      ...(interactive && {
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0px 5px 15px ${alpha(accent, 0.18)}`,
        },
        "&:hover .product-arrow": {
          transform: "translate(3px, -3px)",
          opacity: 1,
          backgroundColor: accent,
          color: "#fff",
        },
      }),
    },
    logoWrap: {
      height: 32,
      display: "flex",
      alignItems: "center",
      maxWidth: "78%",
      overflow: "hidden",
    },
    arrow: {
      width: 30,
      height: 30,
      flexShrink: 0,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: accent,
      backgroundColor: alpha(accent, 0.12),
      opacity: 0.85,
      transition: "transform .18s ease, background-color .18s, color .18s",
      "& svg": { fontSize: 18 },
    },
    desc: {
      fontSize: "0.83rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.35,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
  };

  const renderLogo = () => {
    if (clientImg) {
      return (
        <img
          src={clientImg}
          alt={name}
          style={{
            height: logoHeight,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            objectPosition: "left center",
          }}
        />
      );
    }
    if (logo) {
      // As logos têm a cor da marca embutida; a parte "easy" fica sem cor de
      // propósito para herdar o fill — que deve ser a cor de texto do tema.
      return cloneElement(logo, {
        style: {
          height: logoHeight,
          width: "auto",
          maxWidth: "100%",
          fill: disabled
            ? theme.palette.text.disabled
            : theme.palette.text.primary,
        },
        role: "img",
        "aria-label": name,
      });
    }
    if (icon) {
      // Ícone de biblioteca (não é logo de marca): mostra o ícone colorido
      // com o accent ao lado do nome, para compor um "wordmark" equivalente.
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          {cloneElement(icon, {
            sx: {
              fontSize: logoHeight,
              color: disabled ? theme.palette.text.disabled : accent,
            },
          })}
          <Typography
            sx={{
              fontSize: "1.05rem",
              fontWeight: 700,
              color: disabled
                ? theme.palette.text.disabled
                : theme.palette.text.primary,
            }}
          >
            {name}
          </Typography>
        </Stack>
      );
    }
    return (
      <Typography sx={{ fontSize: "1.05rem", fontWeight: 600 }}>
        {name}
      </Typography>
    );
  };

  return (
    <>
      <Box
        sx={styles.card}
        onClick={() => handleOpen(false)}
        onMouseDown={(e) => {
          if (e.button === 1) handleOpen(true);
        }}
        role={interactive ? "button" : undefined}
        aria-label={name}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={(e) => {
          if (interactive && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleOpen(false);
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
        >
          <Box sx={styles.logoWrap}>{renderLogo()}</Box>
          <Box className="product-arrow" sx={styles.arrow}>
            {disabled ? <LockOutlined /> : <ArrowOutward />}
          </Box>
        </Stack>

        <Typography sx={styles.desc}>{description}</Typography>
      </Box>

      <BlockedModal
        open={blockedModal}
        onClose={() => setBlockedModal(false)}
        productName={productName}
      />
    </>
  );
}
